import React from "react";
import { Settings as SettingsIcon, KeyRound, CheckCircle2, XCircle, Loader2, ShieldCheck, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";

const KEY_LINKS = {
  openai: "https://platform.openai.com/api-keys",
  anthropic: "https://console.anthropic.com/settings/keys",
  gemini: "https://aistudio.google.com/app/apikey",
  grok: "https://console.x.ai",
};

export default function Settings() {
  const t = useT();
  const [loaded, setLoaded] = React.useState(false);
  const [useOwn, setUseOwn] = React.useState(false);
  const [provider, setProvider] = React.useState("openai");
  const [model, setModel] = React.useState("gpt-5.4");
  const [apiKey, setApiKey] = React.useState("");
  const [hasKey, setHasKey] = React.useState(false);
  const [providerModels, setProviderModels] = React.useState({});
  const [activeSource, setActiveSource] = React.useState("builtin");
  const [testing, setTesting] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api.getLlmSettings().then((d) => {
      setUseOwn(d.use_own_key);
      setProvider(d.provider || "openai");
      setModel(d.model || "gpt-5.4");
      setHasKey(d.has_key);
      setProviderModels(d.provider_models || {});
      setActiveSource(d.active_source);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const models = providerModels[provider] || [];

  const onProviderChange = (p) => {
    setProvider(p);
    const first = (providerModels[p] || [])[0];
    if (first) setModel(first);
  };

  const buildPayload = () => ({ use_own_key: useOwn, provider, model, api_key: apiKey || null });

  const doTest = async () => {
    setTesting(true);
    try {
      const res = await api.testLlmSettings(buildPayload());
      if (res.success && res.healthy) toast.success(t("connection_ok"));
      else if (res.success) toast.success(`${t("connection_ok")} (${res.reply})`);
      else toast.error(`${t("connection_failed")}: ${res.error}`);
    } catch (e) {
      toast.error(`${t("connection_failed")}: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setTesting(false);
    }
  };

  const doSave = async () => {
    if (useOwn && !apiKey && !hasKey) {
      toast.error(t("key_required"));
      return;
    }
    setSaving(true);
    try {
      const res = await api.saveLlmSettings(buildPayload());
      setActiveSource(res.active_source);
      if (apiKey) setHasKey(true);
      setApiKey("");
      toast.success(t("settings_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const OptionCard = ({ selected, onClick, icon: Icon, title, desc, testid }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${
        selected ? "border-primary bg-primary/5 ring-1 ring-primary" : "bg-card hover:bg-secondary/50"
      }`}
    >
      <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${selected ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
        <Icon className="h-4 w-4" />
      </span>
      <span>
        <span className="block font-medium">{title}</span>
        <span className="block text-sm text-muted-foreground">{desc}</span>
      </span>
    </button>
  );

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6">
        <div className="mb-1 flex items-center gap-2 text-sm font-medium text-primary"><SettingsIcon className="h-4 w-4" /> {t("nav_settings")}</div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">{t("settings_title")}</h1>
        <p className="text-sm text-muted-foreground">{t("settings_subtitle")}</p>
      </div>

      <Card className="p-6">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">{t("llm_config")}</h2>
          <Badge variant={activeSource === "own" ? "default" : "secondary"} className="gap-1">
            <ShieldCheck className="h-3.5 w-3.5" />
            {activeSource === "own" ? t("active_source_own") : t("active_source_builtin")}
          </Badge>
        </div>
        <p className="mb-5 text-sm text-muted-foreground">{t("llm_config_desc")}</p>

        <div className="grid gap-3 sm:grid-cols-2">
          <OptionCard selected={!useOwn} onClick={() => setUseOwn(false)} icon={CheckCircle2} title={t("use_builtin")} desc={t("use_builtin_desc")} testid="settings-use-builtin" />
          <OptionCard selected={useOwn} onClick={() => setUseOwn(true)} icon={KeyRound} title={t("use_own")} desc={t("use_own_desc")} testid="settings-use-own" />
        </div>

        {useOwn && (
          <div className="mt-5 space-y-4 rounded-xl border bg-secondary/30 p-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs">{t("provider")}</Label>
                <Select value={provider} onValueChange={onProviderChange}>
                  <SelectTrigger className="mt-1" data-testid="settings-provider-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="openai">OpenAI</SelectItem>
                    <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                    <SelectItem value="gemini">Google Gemini</SelectItem>
                    <SelectItem value="grok">Grok (xAI)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t("model")}</Label>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger className="mt-1" data-testid="settings-model-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {models.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">{t("api_key")}</Label>
              <Input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={hasKey ? t("api_key_stored") : t("api_key_placeholder")}
                className="mt-1 font-mono"
                data-testid="settings-api-key-input"
              />
              <a href={KEY_LINKS[provider]} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-xs text-primary hover:underline">
                {t("where_get_key")} <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          <Button variant="outline" onClick={doTest} disabled={testing} data-testid="settings-test-button">
            {testing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />}
            {testing ? t("testing") : t("test_connection")}
          </Button>
          <Button onClick={doSave} disabled={saving} data-testid="settings-save-button">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            {t("save_settings")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
